"use client";

import React from "react";
import { ChartLine, MapPin, Rocket, Scale, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const stages = [{
  title: "Feasibility Study",
  description: "Comprehensive market analysis, regulatory landscape review, and initial cost estimations to validate your expansion goals in the European or Indian markets.",
  badge: "Key Requirement: Market Data",
  icon: ChartLine
}, {
  title: "Legal Entity Formation",
  description: "Navigating local corporate laws to establish the optimal legal structure, ensuring full compliance with international trade agreements and tax efficiency.",
  badge: "Key Requirement: Compliance",
  icon: Scale
}, {
  title: "Site Selection",
  description: "Identifying and securing the ideal physical or virtual locations for manufacturing, warehousing, or corporate offices tailored to your operational needs.",
  badge: "Key Requirement: Infrastructure",
  icon: MapPin
}, {
  title: "Operational Launch",
  description: "Finalizing logistics, hiring key personnel, and initiating core business activities to officially enter the market with a robust foundation.",
  badge: "Key Requirement: Execution",
  icon: Rocket
}];
export default function ExpansionStrategy() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="expansion-strategy" className="relative bg-background py-24 md:py-32 overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-40">
        <div className="absolute inset-0 bg-[radial-gradient(var(--color-border)_1px,transparent_1px)] bg-[size:24px_24px] [mask-image:radial-gradient(ellipse_at_center,black_20%,transparent_70%)]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {}
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-16 md:mb-24 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-primary mb-6 tracking-tight">
            Expansion Strategy
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-sans leading-relaxed">
            A structured, four-stage process designed to seamlessly bridge your operations between Europe and India, minimizing risk and maximizing growth potential.
          </p>
        </div>

        {}
        <div className="max-w-4xl mx-auto relative">
          {}
          <div className="absolute left-[15px] md:left-[23px] top-8 bottom-8 w-[2px]">
            <Separator orientation="vertical" className="h-full w-full bg-border/60" />
          </div>

          <div className="space-y-12 md:space-y-16">
            {stages.map((stage, i) => {
            const {
              ref: itemRef,
              isVisible: isItemVisible
            } = useRevealOnScroll({
              threshold: 0.2
            });
            return <div key={i} data-index={i} ref={itemRef} className={`relative flex items-start gap-6 md:gap-10 transition-all duration-700 ease-out delay-[${i * 100}ms] ${isItemVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
                  {}
                  <div className="relative z-10 flex shrink-0 items-center justify-center w-8 h-8 md:w-12 md:h-12 rounded-full bg-background border-2 border-primary shadow-sm mt-1 md:mt-0 ring-4 ring-background">
                    <stage.icon className="w-4 h-4 md:w-5 md:h-5 text-primary" />
                  </div>

                  {}
                  <div className="flex-1">
                    <Card className="p-6 md:p-8 shadow-sm hover:shadow-md transition-shadow duration-300 border-border/50 bg-card/80 backdrop-blur-sm group">
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-4">
                        <h3 className="font-serif text-2xl md:text-3xl font-semibold text-foreground group-hover:text-primary transition-colors">
                          {stage.title}
                        </h3>
                        <Badge variant="secondary" className="w-fit shrink-0 font-sans">
                          {stage.badge}
                        </Badge>
                      </div>
                      <p className="text-muted-foreground font-sans leading-relaxed text-base md:text-lg">
                        {stage.description}
                      </p>
                    </Card>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {}
        <div className="mt-20 text-center">
          <Button asChild size="lg" className="font-sans rounded-full px-8 shadow-md hover:shadow-lg transition-all">
            <Link to="/MarketEntryPage">
              Start Your Expansion Journey
              <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}